import { useState } from 'react';
import { TriangleAlert } from 'lucide-react';

import Chat from './components/viewAgentPanel/Chat';
import Header from './components/viewAgentPanel/Header';
import History from './components/viewAgentPanel/History';

import { useAgentPanelContext } from '@/app/agentConversation/_context';
import { useAgentPanelResize } from '@/app/agentConversation/_hooks';

import { Button } from '@/app/ui/button';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/app/ui/empty';
import { ErrorBoundary } from '@/app/ui/error';

/**
 * The assistant, docked on the right. Mounted once beside the routes, so it —
 * and a reply it is writing — stays put while the user moves around the app.
 * It takes its width from the app rather than covering it (`--agent-w`), and
 * its left edge drags to resize.
 */
const ViewAgentPanel = () => {
  const panel = useAgentPanelContext();
  if (!panel.isEnabled || !panel.isOpen) return null;
  return <AgentPanel />;
};

const AgentPanel = () => {
  const panel = useAgentPanelContext();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const handleResizeStart = useAgentPanelResize(panel.setWidth);

  const handleToggleHistory = () => setIsHistoryOpen((current) => !current);
  const handleCloseHistory = () => setIsHistoryOpen(false);

  return (
    <aside className="bg-background fixed inset-y-0 right-0 z-40 flex w-(--agent-w) flex-col border-l shadow-sm" aria-label="Assistant" data-testid="agent-panel">
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the assistant"
        onPointerDown={handleResizeStart}
        className="hover:bg-primary/30 active:bg-primary/40 absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize transition-colors"
        data-testid="agent-panel-resize"
      />
      <ErrorBoundary fallback={PanelFallback}>
        <Header isHistoryOpen={isHistoryOpen} onToggleHistory={handleToggleHistory} />
        {isHistoryOpen ? <History onClose={handleCloseHistory} /> : <Chat />}
      </ErrorBoundary>
    </aside>
  );
};

// The whole panel failed: offer a clean start, or getting it out of the way.
const PanelFallback = ({ resetError }) => {
  const panel = useAgentPanelContext();

  const handleNew = () => {
    panel.startNewConversation();
    resetError();
  };

  return (
    <Empty className="border-0" data-testid="agent-panel-error">
      <EmptyHeader>
        <EmptyMedia>
          <TriangleAlert />
        </EmptyMedia>
        <EmptyTitle>The Assistant Hit a Problem</EmptyTitle>
        <EmptyDescription>Our team has been notified. Start a new conversation, or close the assistant and carry on.</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleNew}>
            New Conversation
          </Button>
          <Button size="sm" variant="outline" onClick={panel.close}>
            Close the Assistant
          </Button>
        </div>
      </EmptyContent>
    </Empty>
  );
};

export default ViewAgentPanel;
